import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Plus,
  Edit,
  Package,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Trash2,
  Copy,
  FilterX,
  FileText,
} from 'lucide-react';
import { api } from '../../core/apiClient';
import { formatCurrency } from '../../lib/utils';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

const ELEMENT_TYPE_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-teal-100 text-teal-800',
  'bg-slate-100 text-slate-600',
  'bg-rose-100 text-rose-800',
  'bg-orange-100 text-orange-800',
  'bg-green-100 text-green-800',
];

interface Material {
  id: string;
  code?: string;
  name: string;
  unit: string;
  elementTypeId?: number;
  materialTypeId?: number;
  compositeCodeId?: number;
  weightKgPerUnit: number;
  purchaseCost: number;
  purchaseCostUnit?: string;
  costPerUnit: number;
  pricePerKg: number;
  measurementFactor: number;
  priceFactor: number;
  wastePercentage: number;
  priceDate?: string;
  supplierRef?: string;
  location?: string;
  supplierId?: string;
  supplier?: {
    id: string;
    name: string;
  };
  observations?: string;
  imageUrl?: string | null;
  technicalImageUrl?: string | null;
  isActive: number;
}

interface SelectOption {
  id: number;
  code: string;
  name: string;
}

export default function Materials() {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  const [techImgErrors, setTechImgErrors] = useState<Set<string>>(new Set());
  const [elementTypes, setElementTypes] = useState<SelectOption[]>([]);
  const [materialTypes, setMaterialTypes] = useState<SelectOption[]>([]);
  const [compositeCodes, setCompositeCodes] = useState<SelectOption[]>([]);
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<{ id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean }[]>([]);
  const [compositeModels, setCompositeModels] = useState<{ id: number; code: string; name?: string | null; productId: number; isActive: number | boolean }[]>([]);
  const [compositeVariants, setCompositeVariants] = useState<{ id: number; code: string; name?: string | null; modelId: number; isActive: number | boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch]                           = useSessionState('materials:search', '');
  const [elementTypeFilter, setElementTypeFilter]     = useSessionState<string>('materials:elementTypeFilter', 'all');
  const [materialTypeFilter, setMaterialTypeFilter]   = useSessionState<string>('materials:materialTypeFilter', 'all');
  const [compositeCodeFilter, setCompositeCodeFilter] = useSessionState<string>('materials:compositeCodeFilter', 'all');
  const [compositeSubfamilyFilter, setCompositeSubfamilyFilter] = useSessionState<string>('materials:compositeSubfamilyFilter', 'all');
  const [compositeProductFilter, setCompositeProductFilter] = useSessionState<string>('materials:compositeProductFilter', 'all');
  const [compositeModelFilter, setCompositeModelFilter] = useSessionState<string>('materials:compositeModelFilter', 'all');
  const [compositeVariantFilter, setCompositeVariantFilter] = useSessionState<string>('materials:compositeVariantFilter', 'all');
  const [statusFilter, setStatusFilter]               = useSessionState<string>('materials:statusFilter', 'all');
  const [sortBy, setSortBy]                           = useSessionState<string>('materials:sortBy', 'code');
  const [sortDir, setSortDir]                         = useSessionState<'ASC' | 'DESC'>('materials:sortDir', 'ASC');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);

  const [deleteConfirm, setDeleteConfirm] = useState<Material | null>(null);

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortBy(col); setSortDir('ASC'); }
    setPage(1);
  };
  const sortIcon = (col: string) => sortBy === col
    ? <span className="ml-1 text-foreground">{sortDir === 'ASC' ? '↑' : '↓'}</span>
    : <span className="ml-1 text-muted-foreground/40">↕</span>;

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (elementTypeFilter !== 'all') params.append('elementTypeId', elementTypeFilter);
      if (materialTypeFilter !== 'all') params.append('materialTypeId', materialTypeFilter);
      if (compositeCodeFilter !== 'all') params.append('compositeCodeId', compositeCodeFilter);
      if (compositeSubfamilyFilter !== 'all') params.append('compositeSubfamilyId', compositeSubfamilyFilter);
      if (compositeProductFilter !== 'all') params.append('compositeProductId', compositeProductFilter);
      if (compositeModelFilter !== 'all') params.append('compositeModelId', compositeModelFilter);
      if (compositeVariantFilter !== 'all') params.append('compositeVariantId', compositeVariantFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('sortBy', sortBy);
      params.append('sortDir', sortDir);
      params.append('page', String(page));
      params.append('limit', String(limit));

      const response = await api.get<{
        success: boolean;
        data: Material[];
        pagination: { total: number; page: number; limit: number; totalPages: number };
      }>(`/simple-materials?${params}`);

      setMaterials(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      console.error('[v0] Error fetching materials:', err);
      setError('Error en carregar els materials');
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [search, elementTypeFilter, materialTypeFilter, compositeCodeFilter, compositeSubfamilyFilter, compositeProductFilter, compositeModelFilter, compositeVariantFilter, statusFilter, sortBy, sortDir, page, limit]);

  const fetchOptions = useCallback(async () => {
    const empty = { success: true, data: [] };
    const [etRes, mtRes, ccRes, subRes, prodRes, modelRes, variantRes] = await Promise.all([
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/element-types').catch(() => empty),
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/material-types').catch(() => empty),
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/composite-codes').catch(() => empty),
      api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies').catch(() => empty),
      api.get<{ success: boolean; data: typeof compositeProducts }>('/catalog/composite-products').catch(() => empty),
      api.get<{ success: boolean; data: typeof compositeModels }>('/catalog/composite-models').catch(() => empty),
      api.get<{ success: boolean; data: typeof compositeVariants }>('/catalog/composite-variants').catch(() => empty),
    ]);
    setElementTypes((etRes as { data: SelectOption[] }).data);
    setMaterialTypes((mtRes as { data: SelectOption[] }).data);
    setCompositeCodes(((ccRes as { data: SelectOption[] }).data).sort((a, b) => a.name.localeCompare(b.name, 'ca', { numeric: true })));
    setCompositeSubfamilies((subRes as { data: typeof compositeSubfamilies }).data.filter((s) => Number(s.isActive) === 1));
    setCompositeProducts((prodRes as { data: typeof compositeProducts }).data.filter((p) => Number(p.isActive) === 1));
    setCompositeModels((modelRes as { data: typeof compositeModels }).data.filter((m) => Number(m.isActive) === 1));
    setCompositeVariants((variantRes as { data: typeof compositeVariants }).data.filter((v) => Number(v.isActive) === 1));
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    setPage(1);
  }, [search, elementTypeFilter, materialTypeFilter, compositeCodeFilter, compositeSubfamilyFilter, compositeProductFilter, compositeModelFilter, compositeVariantFilter, statusFilter]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/simple-materials/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      fetchMaterials();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      console.error('[v0] Error deleting material:', err);
      setError(error.response?.data?.error?.message || 'Error en eliminar el material');
    }
  };

  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const handleDuplicate = async (id: string) => {
    setDuplicatingId(id);
    try {
      const res = await api.post<{ success: boolean; data: { id: string } }>(`/simple-materials/${id}/duplicate`);
      if (res.success) navigate(`/compres/materials/${res.data.id}/edit`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Error en duplicar el material');
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Button
            variant="outline"
            title="Netejar tots els filtres"
            onClick={() => {
              setSearch('');
              setElementTypeFilter('all');
              setMaterialTypeFilter('all');
              setCompositeCodeFilter('all');
              setCompositeSubfamilyFilter('all');
              setCompositeProductFilter('all');
              setCompositeModelFilter('all');
              setCompositeVariantFilter('all');
              setStatusFilter('all');
            }}
          >
            <FilterX className="h-4 w-4" />
          </Button>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Cercar per codi o nom..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <ExcelFilterDropdown
            label="Tipus d'elements"
            options={elementTypes.map((et) => ({ value: String(et.id), label: et.name }))}
            selected={elementTypeFilter === 'all' ? [] : elementTypeFilter.split(',')}
            onApply={(values) => setElementTypeFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Família d'elements"
            options={materialTypes.map((mt) => ({ value: String(mt.id), label: mt.name }))}
            selected={materialTypeFilter === 'all' ? [] : materialTypeFilter.split(',')}
            onApply={(values) => setMaterialTypeFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Tipus de producte"
            options={compositeCodes.map((cc) => ({ value: String(cc.id), label: cc.name }))}
            selected={compositeCodeFilter === 'all' ? [] : compositeCodeFilter.split(',')}
            onApply={(values) => setCompositeCodeFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Subfamília"
            options={compositeSubfamilies
              .filter((s) => compositeCodeFilter === 'all' || compositeCodeFilter.split(',').includes(String(s.compositeCodeId)))
              .map((s) => ({ value: String(s.id), label: s.name }))}
            selected={compositeSubfamilyFilter === 'all' ? [] : compositeSubfamilyFilter.split(',')}
            onApply={(values) => setCompositeSubfamilyFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Producte"
            options={compositeProducts
              .filter((p) => compositeSubfamilyFilter === 'all' || compositeSubfamilyFilter.split(',').includes(String(p.subfamilyId)))
              .map((p) => ({ value: String(p.id), label: p.name }))}
            selected={compositeProductFilter === 'all' ? [] : compositeProductFilter.split(',')}
            onApply={(values) => setCompositeProductFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Model"
            options={compositeModels
              .filter((m) => compositeProductFilter === 'all' || compositeProductFilter.split(',').includes(String(m.productId)))
              .map((m) => ({ value: String(m.id), label: m.name || m.code }))}
            selected={compositeModelFilter === 'all' ? [] : compositeModelFilter.split(',')}
            onApply={(values) => setCompositeModelFilter(values.length === 0 ? 'all' : values.join(','))}
          />
          <ExcelFilterDropdown
            label="Variant"
            options={compositeVariants
              .filter((v) => compositeModelFilter === 'all' || compositeModelFilter.split(',').includes(String(v.modelId)))
              .map((v) => ({ value: String(v.id), label: v.name || v.code }))}
            selected={compositeVariantFilter === 'all' ? [] : compositeVariantFilter.split(',')}
            onApply={(values) => setCompositeVariantFilter(values.length === 0 ? 'all' : values.join(','))}
          />
        </div>
        <Button onClick={() => navigate('/compres/materials/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nou Material
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Elements</CardTitle>
            <span className="text-sm text-muted-foreground">{total} elements trobats</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : materials.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
              <Package className="mb-2 h-8 w-8" />
              <span>No s&apos;han trobat elements</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-10">Fitxa tècnica</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('code')}>Codi{sortIcon('code')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('name')}>Nom{sortIcon('name')}</TableHead>
                  <TableHead>Tipus</TableHead>
                  <TableHead className="text-right">Amidament</TableHead>
                  <TableHead className="text-center">Pes</TableHead>
                  <TableHead className="text-right cursor-pointer select-none hover:text-foreground whitespace-nowrap" onClick={() => toggleSort('purchaseCost')}>Cost Compra{sortIcon('purchaseCost')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('costPerUnit')}>Cost/UA{sortIcon('costPerUnit')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none hover:text-foreground whitespace-nowrap" onClick={() => toggleSort('pricePerKg')}>Preu/Kg efectiu{sortIcon('pricePerKg')}</TableHead>
                  <TableHead className="text-right">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map((material) => (
                  <TableRow key={material.id} className="cursor-pointer hover:bg-accent/50 whitespace-nowrap" onClick={() => navigate(`/compres/materials/${material.id}/edit`)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {material.imageUrl && !imgErrors.has(material.id) ? (
                        <img
                          src={material.imageUrl}
                          alt={material.name}
                          className="h-8 w-8 rounded object-cover border"
                          onError={() => setImgErrors((prev) => new Set(prev).add(material.id))}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded border bg-slate-100 flex items-center justify-center">
                          <Package className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {material.technicalImageUrl && !techImgErrors.has(material.id) ? (
                        <img
                          src={material.technicalImageUrl}
                          alt={`${material.name} — fitxa tècnica`}
                          className="h-8 w-auto max-w-[64px] rounded object-contain border"
                          onError={() => setTechImgErrors((prev) => new Set(prev).add(material.id))}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded border bg-slate-100 flex items-center justify-center">
                          <FileText className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{material.code || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{material.name}</span>
                        {material.supplierRef && (
                          <span className="text-xs text-muted-foreground">Ref: {material.supplierRef}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const etIdx = elementTypes.findIndex(et => et.id === Number(material.elementTypeId));
                        const et = etIdx >= 0 ? elementTypes[etIdx] : null;
                        return et
                          ? <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ELEMENT_TYPE_COLORS[etIdx % ELEMENT_TYPE_COLORS.length]}`}>{et.name}</span>
                          : <span className="text-xs text-muted-foreground">—</span>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(material.measurementFactor || 1).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {material.unit && (
                        <span className="ml-0.5 text-xs text-muted-foreground">{material.unit}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(material.weightKgPerUnit).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {material.unit && (
                        <span className="ml-0.5 text-xs text-muted-foreground">Kg/{material.unit}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span>{formatCurrency(Number(material.purchaseCost))}</span>
                      {material.purchaseCostUnit && (
                        <span className="ml-0.5 text-xs text-muted-foreground">/{material.purchaseCostUnit}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(() => {
                        const wp = Number(material.wastePercentage || 0);
                        const wasteMultiplier = wp < 100 ? 1 / (1 - wp / 100) : 1;
                        const cost = formatCurrency(Number(material.purchaseCost || 0) * Number(material.priceFactor || 1) * wasteMultiplier);
                        return (
                          <>
                            <span>{cost}</span>
                            {material.unit && (
                              <span className="ml-0.5 text-xs text-muted-foreground">/{material.unit}</span>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(() => {
                        const wp = Number(material.wastePercentage || 0);
                        const wasteMultiplier = wp < 100 ? 1 / (1 - wp / 100) : 1;
                        const pricePerUnit = Number(material.purchaseCost || 0) * Number(material.priceFactor || 1) * wasteMultiplier;
                        const kg = Number(material.weightKgPerUnit || 0);
                        return (
                          <>
                            <span>{formatCurrency(kg > 0 ? pricePerUnit / kg : 0)}</span>
                            <span className="ml-0.5 text-xs text-muted-foreground">/Kg</span>
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/compres/materials/${material.id}/edit`)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDuplicate(material.id)}
                          disabled={duplicatingId === material.id}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-40"
                          title="Duplicar"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(material)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Mostrar</span>
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="text-xs border border-input rounded px-2 py-1 bg-background focus:outline-none"
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
              <span className="text-xs text-muted-foreground">per pàgina · {total} elements</span>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Pàgina {page} de {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminació</DialogTitle>
          </DialogHeader>
          <p>
            Estas segur que vols eliminar el material <strong>{deleteConfirm?.name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel·lar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
